// summaryStats / bestRewrite / timelineSeries — the Summary screen's pure
// derivations (match-screen design D-sum-2/D-sum-3; client-summary "Summary stat
// tiles"/"Best rewrite highlight"). Pure over the store's `matchTimeline`/
// `promptCycleCount`/`provenanceLog`: no store read, no dispatch, no rendering —
// `Summary.tsx` (group 7) renders what these derive.
//
// The completeness contract (design D-sum-1, F3) is the load-bearing gate here:
// `matchTimeline.complete` is `false` for any session that didn't observe the
// match from tick 0 (a mid-match join/reconnect). A partial series could report
// a false extremum, and the `promptCycleCount` accumulator (which also only
// begins at connect) would render an authoritative-looking undercount — so both
// peak bees and prompts sent are suppressed together, and Best rewrite is
// omitted outright, whenever the timeline is `partial`.

import type { ScoreboardRow } from '../../net/wire/messages';
import type { MatchTimeline, VersionProvenance } from '../../state/store';

/** The locked three-tile stat row's derived values (design D-sum-2). `null`
 * renders as "—": always for `nectarGathered` (no cumulative wire source,
 * D-076), and for `peakBees`/`promptsSent` whenever the timeline is `partial`. */
export interface SummaryStats {
  peakBees: number | null;
  promptsSent: number | null;
  nectarGathered: null;
}

/**
 * Derives the stat tiles from the accreted `matchTimeline`, the
 * `promptCycleCount` accumulator, and the own colony id (`SessionSlice.colonyId`).
 * Both `peakBees` and `promptsSent` are suppressed together when
 * `matchTimeline.complete` is `false`, or when `ownColonyId` hasn't resolved yet.
 */
export function summaryStats(
  matchTimeline: MatchTimeline,
  promptCycleCount: number,
  ownColonyId: number | null,
): SummaryStats {
  if (!matchTimeline.complete || ownColonyId === null) {
    return { peakBees: null, promptsSent: null, nectarGathered: null };
  }

  let peakBees: number | null = null;
  for (const sample of matchTimeline.samples) {
    const row = sample.scoreboard.find((r) => r.colonyId === ownColonyId);
    if (row !== undefined && (peakBees === null || row.livingCount > peakBees)) {
      peakBees = row.livingCount;
    }
  }

  return { peakBees, promptsSent: promptCycleCount, nectarGathered: null };
}

/** The "🏅 Best rewrite" highlight's referent (design D-sum-3): a version
 * identified by `(typeId, version)` (parsed off its `provenanceLog` key,
 * `versionCacheKey`) plus its accreted provenance. The player's semantic prompt
 * text is never echoed to the client (D-078) — callers quote `provenance` (a
 * tick + repair count) via `formatProvenanceSummary`, never a prompt string. */
export interface BestRewrite {
  typeId: number;
  version: number;
  provenance: VersionProvenance;
}

function parseVersionKey(key: string): { typeId: number; version: number } {
  const [typeIdStr, versionStr] = key.split(':');
  return { typeId: Number(typeIdStr), version: Number(versionStr) };
}

/**
 * Identifies the accepted version whose activation most immediately precedes
 * the own colony's steepest sustained `livingCount` gain (design D-sum-3): the
 * classic "buy low, sell high" scan over the own-colony timeline series finds
 * the largest rise from a trough to a later point, and the trough's tick anchors
 * "most immediately precedes" — the candidate with the *latest* `acceptedTick`
 * at or before that trough wins, i.e. the version most recently active when the
 * climb began. Returns `null` when fewer than two versions have observed
 * provenance, the own timeline shows no discernible gain (flat or declining), no
 * candidate precedes the gain, or the timeline is `partial` (client-summary
 * "Best rewrite is omitted when data is insufficient or the timeline is
 * partial").
 */
export function bestRewrite(
  matchTimeline: MatchTimeline,
  provenanceLog: Record<string, VersionProvenance>,
  ownColonyId: number | null,
): BestRewrite | null {
  if (!matchTimeline.complete) return null;

  const entries = Object.entries(provenanceLog).map(([key, provenance]) => ({
    ...parseVersionKey(key),
    provenance,
  }));
  if (entries.length < 2) return null;
  if (ownColonyId === null) return null;

  const ownSeries = matchTimeline.samples
    .map((s) => ({ tick: s.tick, row: s.scoreboard.find((r) => r.colonyId === ownColonyId) }))
    .filter((p): p is { tick: number; row: ScoreboardRow } => p.row !== undefined)
    .map((p) => ({ tick: p.tick, livingCount: p.row.livingCount }));
  if (ownSeries.length === 0) return null;

  let trough = ownSeries[0];
  let bestGain = 0;
  let bestGainStartTick = ownSeries[0].tick;
  for (let i = 1; i < ownSeries.length; i++) {
    const point = ownSeries[i];
    const gain = point.livingCount - trough.livingCount;
    if (gain > bestGain) {
      bestGain = gain;
      bestGainStartTick = trough.tick;
    }
    // `<=` (not `<`): the *latest* occurrence of the running minimum anchors the
    // climb's start closest in time to the gain, not its earliest occurrence.
    if (point.livingCount <= trough.livingCount) {
      trough = point;
    }
  }
  if (bestGain <= 0) return null; // flat or declining: no discernible gain

  const candidates = entries.filter((e) => e.provenance.acceptedTick <= bestGainStartTick);
  if (candidates.length === 0) return null;

  candidates.sort((a, b) => b.provenance.acceptedTick - a.provenance.acceptedTick);
  const winner = candidates[0];
  return { typeId: winner.typeId, version: winner.version, provenance: winner.provenance };
}

/** One colony's shaped timeline series: one point per sample where that colony
 * had a scoreboard row. */
export interface TimelineColonySeries {
  colonyId: number;
  points: Array<{ tick: number; livingCount: number }>;
}

/** The Summary population-timeline SVG's source shape (design D-sum-1): the
 * accreted `matchTimeline`, reshaped from "one sample per tick, all colonies"
 * into "one series per colony", carrying the completeness flag through
 * unchanged so the chart can mark itself partial (client-summary "a partial
 * timeline is marked partial"). */
export interface TimelineSeries {
  complete: boolean;
  series: TimelineColonySeries[];
}

/** Shapes `matchTimeline` into per-colony point series, sorted by colony id for
 * a deterministic draw order. */
export function timelineSeries(matchTimeline: MatchTimeline): TimelineSeries {
  const colonyIds = new Set<number>();
  for (const sample of matchTimeline.samples) {
    for (const row of sample.scoreboard) colonyIds.add(row.colonyId);
  }

  const series: TimelineColonySeries[] = Array.from(colonyIds)
    .sort((a, b) => a - b)
    .map((colonyId) => ({
      colonyId,
      points: matchTimeline.samples
        .map((s) => ({ tick: s.tick, row: s.scoreboard.find((r) => r.colonyId === colonyId) }))
        .filter((p): p is { tick: number; row: ScoreboardRow } => p.row !== undefined)
        .map((p) => ({ tick: p.tick, livingCount: p.row.livingCount })),
    }));

  return { complete: matchTimeline.complete, series };
}
